import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import db from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, query, where, addDoc } from 'firebase/firestore';
import { combineBarcode } from '../utils/barcodeUtils';
import { buildProductPhotos } from '../utils/productPhotos';
import { getCollectionName, getCountryLabel, isSimpleProductCountry } from '../utils/countryConfig';
import { saveProductPatch } from '../utils/productPersistence';
import { createVariantId } from '../utils/productVariants';
import { normalizeProductEditor, buildProductEditorPatch, toggleEditorVariantActive } from '../utils/productEditor';
import ProductSamples from './ProductSamples';
import ZxingBarcodeScanner from './ZxingBarcodeScanner';
import '../styles.css';

function EditProduct() {
  const { country, id } = useParams();
  const isCrystal = isSimpleProductCountry(country);
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => normalizeProductEditor({}, country));
  const [baseProduct, setBaseProduct] = useState(null);
  const [originalForm, setOriginalForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const [loadError, setLoadError] = useState('');
  const [enableMultiCostWeight, setEnableMultiCostWeight] = useState(false);
  const [enableMultiImage, setEnableMultiImage] = useState(false);
  const [enableMultiPrice, setEnableMultiPrice] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const loadEditorProduct = useCallback((data) => {
    const normalized = normalizeProductEditor(data, country);
    setBaseProduct(data);
    setOriginalForm(normalized);
    setFormData(normalized);
    setEnableMultiCostWeight(normalized.enableMultiCostWeight);
    setEnableMultiImage(normalized.enableMultiImage);
    setEnableMultiPrice(normalized.enableMultiPrice);
  }, [country]);

  useEffect(() => {
    let cancelled = false;
    setBaseProduct(null);
    setOriginalForm(null);
    setLoadError('');
    const fetchProduct = async () => {
      try {
        const collectionName = getCollectionName(country);
        const docRef = doc(db, collectionName, id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (!cancelled) loadEditorProduct(data);
        } else {
          if (!cancelled) setLoadError('找不到商品，請回到商品列表重新開啟。');
        }
      } catch (error) {
        console.error("Error fetching product: ", error);
        if (!cancelled) setLoadError('商品讀取失敗，請重新整理後再試。');
      }
    };
    fetchProduct();
    return () => { cancelled = true; };
  }, [country, id, loadEditorProduct]);

  useEffect(() => {
    const fetchVendors = async () => {
      const vendorsRef = collection(db, 'vendors');
      const q = query(vendorsRef, where('country', '==', country));
      const snapshot = await getDocs(q);
      const vendorList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVendors(vendorList);
    };
    fetchVendors();
  }, [country]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'vendor') {
      localStorage.setItem('lastSelectedVendor', value.trim());
    }
  };

  // 根據 localStorage 中最後選擇的廠商排序，該廠商排最前
  const lastSelectedVendor = localStorage.getItem('lastSelectedVendor') || '';
  const sortedVendors = [...vendors].sort((a, b) => {
    if (a.name === lastSelectedVendor) return -1;
    if (b.name === lastSelectedVendor) return 1;
    return a.name.localeCompare(b.name);
  });

  const handleCostTypeChange = (e) => {
    const costType = e.target.value;
    setFormData(prev => ({ ...prev, costType }));
  };

  const addNewVendor = async (vendorName) => {
    if (!vendorName.trim()) return;
    const vendorsRef = collection(db, 'vendors');
    const q = query(vendorsRef, where('country', '==', country), where('name', '==', vendorName));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      const docRef = await addDoc(vendorsRef, { name: vendorName, country });
      setVendors(prev => [...prev, { id: docRef.id, name: vendorName, country }]);
    }
  };

  const handleSave = async () => {
    if (savingRef.current || !baseProduct || !originalForm) return;
    if (formData.newStyle.trim() || formData.newStyleBarcode.trim()) {
      alert('您有尚未加入的樣式，請先按「新增樣式」或清空新增欄位再保存。');
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    try {
      const patch = buildProductEditorPatch(baseProduct, originalForm, {
        ...formData, enableMultiCostWeight, enableMultiImage, enableMultiPrice,
      });
      const saved = await saveProductPatch({ country, id, base: baseProduct, patch });
      if (formData.vendor.trim() && !vendors.some(v => v.name === formData.vendor.trim())) {
        try {
          await addNewVendor(formData.vendor.trim());
        } catch (error) {
          console.error('商品已保存，但新增廠商清單失敗:', error);
        }
      }
      loadEditorProduct(saved);
      alert(`${getCountryLabel(country)}商品資料已成功更新`);
      navigate(`/view/${country}/${id}`);
    } catch (error) {
      console.error('商品更新失敗:', error);
      if (error.code === 'permission-denied') {
        alert('權限不足，無法保存商品資料。請確認您已登入管理系統。');
      } else {
        alert(`保存失敗：${error.message || '未知錯誤'}\n您的修改仍保留在此頁，可以稍後重試。`);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const addStyle = () => {
    const { newStyle, newStyleBarcode, newStyleImage, itemNumber } = formData;
    const style = newStyle.trim() === '' ? '無樣式' : newStyle.trim();
    if (newStyleBarcode.trim() !== '' || (isCrystal && newStyle.trim())) {
      const rawBarcode = newStyleBarcode.trim();
      const combinedBarcode = combineBarcode(rawBarcode, itemNumber);
      
      // 根據開關狀態準備樣式詳細信息
      const styleDetail = {
        active: true,
        image: enableMultiImage ? (newStyleImage || null) : null,
        livePrice: enableMultiPrice ? (formData.livePrice ?? null) : null,
        onlinePrice: enableMultiPrice ? (formData.onlinePrice ?? null) : null,
        costKoreanWon: enableMultiCostWeight ? (formData.costKoreanWon ?? null) : null,
        costJPY: enableMultiCostWeight ? (formData.costJPY ?? null) : null,
        costTWD: enableMultiCostWeight ? (formData.costTWD ?? null) : null,
        costTHB: enableMultiCostWeight ? (formData.costTHB ?? null) : null,
        costRMB: enableMultiCostWeight ? (formData.costRMB ?? null) : null,
        weight: enableMultiCostWeight ? (formData.weight ?? null) : null,
        costType: enableMultiCostWeight ? (formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')) : null
      };
      
      setFormData(prev => ({
        ...prev,
        styles: [...prev.styles, style],
        styleIds: [...prev.styleIds, createVariantId()],
        styleBarcodes: [...prev.styleBarcodes, combinedBarcode], // 組合條碼
        rawStyleBarcodes: [...(prev.rawStyleBarcodes || []), rawBarcode], // 原始條碼
        styleDetails: [...(prev.styleDetails || []), styleDetail], // 樣式詳細信息
        newStyle: '',
        newStyleBarcode: '',
        newStyleImage: ''
      }));
    }
  };

  const handleBarcodeScan = (barcode) => {
    setFormData(prev => ({ ...prev, newStyleBarcode: barcode.trim() }));
    setIsScannerOpen(false);
  };

  // Keep all existing slots and references; retiring a style is reversible.
  const toggleStyleActive = (styleId) => {
    setFormData(prev => toggleEditorVariantActive(prev, styleId));
  };

  const updateStyleDetail = (styleId, field, value) => {
    setFormData(prev => ({
      ...prev,
      styleDetails: prev.styleIds.map((id, index) => id === styleId
        ? { ...prev.styleDetails[index], [field]: value ?? '' }
        : prev.styleDetails[index]),
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const url = reader.result;
      setFormData(prev => {
        const photos = buildProductPhotos({
          ...prev,
          image: url,
          photos: [
            { url, source: 'upload', label: '主圖', isCover: true, createdAt: new Date().toISOString() },
            ...(prev.photos || []).map(photo => ({ ...photo, isCover: false })),
          ],
        });
        return { ...prev, image: url, photos };
      });
    };
    reader.readAsDataURL(file);
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#f8f9fa',
    padding: '1rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxWidth: '480px',
    margin: '0 auto'
  };

  const formGroupStyle = {
    marginBottom: '1.25rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#666',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  };

  const buttonStyle = {
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s'
  };

  const scannerButtonStyle = {
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #dbeafe',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '0.75rem'
  };

  const scannerPanelStyle = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '0.75rem',
    marginBottom: '0.75rem'
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#333', textAlign: 'center' }}>
          {isCrystal ? '編輯水晶商品' : '編輯商品'}
        </h2>
        <div style={formGroupStyle}>
          <label style={labelStyle}>名稱</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} style={inputStyle} />
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>料號</label>
          <input type="text" name="itemNumber" value={formData.itemNumber} onChange={handleChange} style={inputStyle} />
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle} htmlFor="product-effects">商品功效 / 介紹</label>
          <textarea id="product-effects" name="effects" value={formData.effects} onChange={handleChange} rows={5} style={inputStyle} placeholder="輸入商品功效、使用方式或直播介紹" />
        </div>
        {!isCrystal && (
          <>
            <div style={formGroupStyle}>
              <label style={labelStyle}>直播價</label>
              <input type="text" name="livePrice" value={formData.livePrice} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>商城價</label>
              <input type="text" name="onlinePrice" value={formData.onlinePrice} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>備註</label>
              <input type="text" name="remarks" value={formData.remarks} onChange={handleChange} style={inputStyle} />
            </div>
            {!enableMultiCostWeight && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>成本類型</label>
                <select name="costType" value={formData.costType} onChange={handleCostTypeChange} style={inputStyle}>
                  <option value="TWD">台幣</option>
                  {country === 'korea' && <option value="KRW">韓幣</option>}
                  {country === 'japan' && <option value="JPY">日幣</option>}
                  {country === 'thailand' && <option value="THB">泰銖</option>}
                  {country === 'china' && <option value="RMB">人民幣</option>}
                </select>
              </div>
            )}
            <div style={formGroupStyle}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={enableMultiCostWeight}
                  onChange={(e) => {
                    setEnableMultiCostWeight(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => {
                        const newStyleDetails = prev.styles.map((_, index) => {
                          const existing = prev.styleDetails?.[index] || {};
                          return {
                            ...existing,
                            image: existing.image ?? null,
                            livePrice: existing.livePrice ?? null,
                            onlinePrice: existing.onlinePrice ?? null,
                            costKoreanWon: existing.costKoreanWon ?? prev.costKoreanWon ?? null,
                            costJPY: existing.costJPY ?? prev.costJPY ?? null,
                            costTWD: existing.costTWD ?? prev.costTWD ?? null,
                            costTHB: existing.costTHB ?? prev.costTHB ?? null,
                            costRMB: existing.costRMB ?? prev.costRMB ?? null,
                            weight: existing.weight ?? prev.weight ?? null,
                            costType: existing.costType ?? prev.costType ?? (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')
                          };
                        });
                        return {
                          ...prev,
                          styleDetails: newStyleDetails
                        };
                      });
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>多成本/重量（每個樣式可設定不同成本和重量）</span>
              </label>
            </div>
            <div style={formGroupStyle}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={enableMultiPrice}
                  onChange={(e) => {
                    setEnableMultiPrice(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => {
                        const newStyleDetails = prev.styles.map((_, index) => {
                          const existing = prev.styleDetails?.[index] || {};
                          return {
                            ...existing,
                            image: existing.image ?? null,
                            livePrice: existing.livePrice ?? prev.livePrice ?? null,
                            onlinePrice: existing.onlinePrice ?? prev.onlinePrice ?? null,
                            costKoreanWon: existing.costKoreanWon ?? null,
                            costJPY: existing.costJPY ?? null,
                            costTWD: existing.costTWD ?? null,
                            costTHB: existing.costTHB ?? null,
                            weight: existing.weight ?? null
                          };
                        });
                        return {
                          ...prev,
                          styleDetails: newStyleDetails
                        };
                      });
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>多價格（每個樣式可設定不同直播價和商城價）</span>
              </label>
            </div>
            {!enableMultiPrice && (
              <>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>直播價</label>
                  <input type="text" name="livePrice" value={formData.livePrice} onChange={handleChange} style={inputStyle} />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>商城價</label>
                  <input type="text" name="onlinePrice" value={formData.onlinePrice} onChange={handleChange} style={inputStyle} />
                </div>
              </>
            )}
            {!enableMultiCostWeight && (
              <>
                {formData.costType === 'TWD' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>成本 (台幣)</label>
                    <input type="text" name="costTWD" value={formData.costTWD} onChange={handleChange} style={inputStyle} />
                  </div>
                )}
                {formData.costType === 'KRW' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>成本 (韓幣)</label>
                    <input type="text" name="costKoreanWon" value={formData.costKoreanWon} onChange={handleChange} style={inputStyle} />
                  </div>
                )}
                {formData.costType === 'JPY' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>成本 (日幣)</label>
                    <input type="text" name="costJPY" value={formData.costJPY} onChange={handleChange} style={inputStyle} />
                  </div>
                )}
                {formData.costType === 'THB' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>成本 (泰銖)</label>
                    <input type="text" name="costTHB" value={formData.costTHB} onChange={handleChange} style={inputStyle} />
                  </div>
                )}
                {formData.costType === 'RMB' && (
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>成本 (人民幣)</label>
                    <input type="text" name="costRMB" value={formData.costRMB} onChange={handleChange} style={inputStyle} />
                  </div>
                )}
                <div style={formGroupStyle}>
                  <label style={labelStyle}>重量</label>
                  <input type="text" name="weight" value={formData.weight} onChange={handleChange} style={inputStyle} />
                </div>
              </>
            )}
          </>
        )}

        {!isCrystal && (
          <div style={formGroupStyle}>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enableMultiImage}
                onChange={(e) => {
                  setEnableMultiImage(e.target.checked);
                  if (e.target.checked) {
                    setFormData(prev => {
                      const newStyleDetails = prev.styles.map((_, index) => {
                        const existing = prev.styleDetails?.[index] || {};
                        return {
                          ...existing,
                          image: existing.image ?? prev.image ?? null,
                          livePrice: existing.livePrice ?? null,
                          onlinePrice: existing.onlinePrice ?? null,
                          costKoreanWon: existing.costKoreanWon ?? null,
                          costJPY: existing.costJPY ?? null,
                          costTWD: existing.costTWD ?? null,
                          costTHB: existing.costTHB ?? null,
                          weight: existing.weight ?? null
                        };
                      });
                      return {
                        ...prev,
                        styleDetails: newStyleDetails
                      };
                    });
                  }
                }}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>多圖片（每個樣式可設定不同圖片）</span>
            </label>
          </div>
        )}

        <div style={formGroupStyle}>
          <label style={labelStyle}>新增樣式</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <input type="text" name="newStyle" value={formData.newStyle} onChange={handleChange} placeholder="樣式名稱" style={{ ...inputStyle, flex: 1, minWidth: '150px' }} />
            {!isCrystal && (
              <input type="text" name="newStyleBarcode" value={formData.newStyleBarcode} onChange={handleChange} placeholder="樣式條碼" style={{ ...inputStyle, flex: 1, minWidth: '150px' }} />
            )}
            {(isCrystal || enableMultiImage) && (
              <input type="text" name="newStyleImage" value={formData.newStyleImage} onChange={handleChange} placeholder="樣式圖片網址" style={{ ...inputStyle, flex: 1, minWidth: '200px' }} />
            )}
          </div>
          {!isCrystal && (
            <>
              <button
                type="button"
                onClick={() => setIsScannerOpen((prev) => !prev)}
                style={scannerButtonStyle}
              >
                {isScannerOpen ? '關閉掃描' : '手機掃條碼'}
              </button>
              {isScannerOpen && (
                <div style={scannerPanelStyle}>
                  <ZxingBarcodeScanner scanning={isScannerOpen} onScan={handleBarcodeScan} />
                </div>
              )}
            </>
          )}
          <button 
            onClick={addStyle}
            style={{
              background: '#f5f5f5',
              color: '#333',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.target.style.background = '#eee'; }}
            onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
          >
            新增樣式
          </button>
        </div>
        {formData.styles.length > 0 && (
          <div style={formGroupStyle}>
            <label style={labelStyle}>樣式列表</label>
            <div style={{ background: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
              {formData.styles.map((style, index) => {
                // 顯示原始條碼（不顯示料號）
                const displayBarcode = formData.rawStyleBarcodes?.[index] || formData.styleBarcodes[index] || '';
                const styleDetail = formData.styleDetails?.[index] || {};
                const styleId = formData.styleIds[index];
                const isActive = styleDetail.active !== false;
                return (
                  <div key={styleId} style={{ opacity: isActive ? 1 : 0.65, padding: '0.75rem', marginBottom: index < formData.styles.length - 1 ? '0.75rem' : '0', borderBottom: index < formData.styles.length - 1 ? '1px solid #e5e5e5' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: (enableMultiCostWeight || enableMultiImage || enableMultiPrice) ? '0.75rem' : '0' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>{style}{!isActive && '（已停用）'}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>條碼: {displayBarcode}</div>
                      </div>
                      <button 
                        onClick={() => toggleStyleActive(styleId)}
                        style={{
                          background: 'transparent',
                          color: '#ff4444',
                          border: '1px solid #ff4444',
                          borderRadius: '6px',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          marginLeft: '1rem'
                        }}
                        onMouseOver={(e) => { e.target.style.background = '#ff4444'; e.target.style.color = '#fff'; }}
                        onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#ff4444'; }}
                      >
                        {isActive ? '停用' : '恢復'}
                      </button>
                    </div>
                    {/* 樣式詳細信息輸入框（根據開關顯示） */}
                    {isActive && (enableMultiCostWeight || enableMultiImage || enableMultiPrice) && (
                      <div style={{ padding: '0.75rem', background: '#fff', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                        {/* 多圖片模式：顯示圖片輸入框 */}
                        {enableMultiImage && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <label style={{ ...labelStyle, fontSize: '0.9rem', marginBottom: '0.25rem', display: 'block' }}>樣式圖片</label>
                            <input
                              type="text"
                              value={styleDetail.image || ''}
                              onChange={(e) => updateStyleDetail(styleId, 'image', e.target.value)}
                              placeholder={`圖片網址 (自動帶入: ${formData.image || '無'})`}
                              style={{ ...inputStyle, fontSize: '0.9rem' }}
                            />
                          </div>
                        )}
                        {/* 多價格模式：顯示價格輸入框 */}
                        {enableMultiPrice && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <div>
                              <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>直播價</label>
                              <input
                                type="text"
                                value={styleDetail.livePrice ?? ''}
                                onChange={(e) => updateStyleDetail(styleId, 'livePrice', e.target.value)}
                                placeholder={`直播價 (自動帶入: ${formData.livePrice ?? '無'})`}
                                style={{ ...inputStyle, fontSize: '0.9rem' }}
                              />
                            </div>
                            <div>
                              <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>商城價</label>
                              <input
                                type="text"
                                value={styleDetail.onlinePrice ?? ''}
                                onChange={(e) => updateStyleDetail(styleId, 'onlinePrice', e.target.value)}
                                placeholder={`商城價 (自動帶入: ${formData.onlinePrice ?? '無'})`}
                                style={{ ...inputStyle, fontSize: '0.9rem' }}
                              />
                            </div>
                          </div>
                        )}
                        {/* 多成本/重量模式：顯示成本和重量輸入框 */}
                        {enableMultiCostWeight && (
                          <div>
                            {/* 成本類型選擇器 */}
                            <div style={{ marginBottom: '0.5rem' }}>
                              <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>成本類型</label>
                              <select
                                value={styleDetail.costType || formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'RMB' : 'THB')}
                                onChange={(e) => updateStyleDetail(styleId, 'costType', e.target.value)}
                                style={{ ...inputStyle, fontSize: '0.9rem' }}
                              >
                                <option value="TWD">台幣</option>
                                {country === 'korea' && <option value="KRW">韓幣</option>}
                                {country === 'japan' && <option value="JPY">日幣</option>}
                                {country === 'thailand' && <option value="THB">泰銖</option>}
                                {country === 'china' && <option value="RMB">人民幣</option>}
                              </select>
                            </div>
                            {/* 根據選擇的成本類型顯示對應的成本輸入框 */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              {(styleDetail.costType || formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')) === 'KRW' && (
                                <div>
                                  <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>成本韓幣</label>
                                  <input
                                    type="text"
                                    value={styleDetail.costKoreanWon ?? ''}
                                    onChange={(e) => updateStyleDetail(styleId, 'costKoreanWon', e.target.value)}
                                    placeholder={formData.costKoreanWon ?? '自動帶入'}
                                    style={{ ...inputStyle, fontSize: '0.9rem' }}
                                  />
                                </div>
                              )}
                              {(styleDetail.costType || formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')) === 'JPY' && (
                                <div>
                                  <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>成本日幣</label>
                                  <input
                                    type="text"
                                    value={styleDetail.costJPY ?? ''}
                                    onChange={(e) => updateStyleDetail(styleId, 'costJPY', e.target.value)}
                                    placeholder={formData.costJPY ?? '自動帶入'}
                                    style={{ ...inputStyle, fontSize: '0.9rem' }}
                                  />
                                </div>
                              )}
                              {(styleDetail.costType || formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')) === 'TWD' && (
                                <div>
                                  <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>成本台幣</label>
                                  <input
                                    type="text"
                                    value={styleDetail.costTWD ?? ''}
                                    onChange={(e) => updateStyleDetail(styleId, 'costTWD', e.target.value)}
                                    placeholder={formData.costTWD ?? '自動帶入'}
                                    style={{ ...inputStyle, fontSize: '0.9rem' }}
                                  />
                                </div>
                              )}
                              {(styleDetail.costType || formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')) === 'THB' && (
                                <div>
                                  <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>成本泰銖</label>
                                  <input
                                    type="text"
                                    value={styleDetail.costTHB ?? ''}
                                    onChange={(e) => updateStyleDetail(styleId, 'costTHB', e.target.value)}
                                    placeholder={formData.costTHB ?? '自動帶入'}
                                    style={{ ...inputStyle, fontSize: '0.9rem' }}
                                  />
                                </div>
                              )}
                              {(styleDetail.costType || formData.costType || (country === 'korea' ? 'KRW' : country === 'japan' ? 'JPY' : country === 'china' ? 'TWD' : 'THB')) === 'RMB' && (
                                <div>
                                  <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>成本人民幣</label>
                                  <input
                                    type="text"
                                    value={styleDetail.costRMB ?? ''}
                                    onChange={(e) => updateStyleDetail(styleId, 'costRMB', e.target.value)}
                                    placeholder={formData.costRMB ?? '自動帶入'}
                                    style={{ ...inputStyle, fontSize: '0.9rem' }}
                                  />
                                </div>
                              )}
                              <div>
                                <label style={{ ...labelStyle, fontSize: '0.85rem', marginBottom: '0.25rem', display: 'block' }}>重量</label>
                                <input
                                  type="text"
                                  value={styleDetail.weight ?? ''}
                                  onChange={(e) => updateStyleDetail(styleId, 'weight', e.target.value)}
                                  placeholder={formData.weight ?? '自動帶入'}
                                  style={{ ...inputStyle, fontSize: '0.9rem' }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={formGroupStyle}>
          <label style={labelStyle}>商品圖片</label>
          <input type="file" onChange={handleImageUpload} style={inputStyle} />
          {formData.image && <img src={formData.image} alt="商品圖片" style={{ width: '100%', maxWidth: '300px', marginTop: '0.75rem', borderRadius: '8px' }} />}
        </div>
        {!isCrystal && (
        <div style={formGroupStyle}>
          <label style={labelStyle}>廠商</label>
          <input 
            type="text" 
            name="vendor" 
            value={formData.vendor} 
            onChange={handleChange} 
            list="vendorList" 
            placeholder="選擇或輸入廠商名稱" 
            style={inputStyle}
          />
          <datalist id="vendorList">
            {sortedVendors.map(vendor => (
              <option key={vendor.id} value={vendor.name} />
            ))}
          </datalist>
        </div>
        )}
        {loadError && <p role="alert" style={{ color: '#b91c1c' }}>{loadError}</p>}
        <button 
          onClick={handleSave} aria-label="儲存商品"
          disabled={isSaving || !baseProduct}
          style={{ ...buttonStyle, opacity: isSaving || !baseProduct ? 0.6 : 1 }}
          onMouseOver={(e) => { e.target.style.background = '#555'; }}
          onMouseOut={(e) => { e.target.style.background = '#333'; }}
        >
          {isSaving ? '保存中...' : '保存商品資料'}
        </button>
        {baseProduct && (
          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e5e5' }}>
            <ProductSamples country={country} productId={id} product={baseProduct} />
          </div>
        )}
      </div>
    </div>
  );
}

export default EditProduct;
